/**
 * Landing query for the Compute tab: every `:Compute` host with the
 * per-host counts the grid renders in one round-trip — direct
 * `HAS_TAG_PROVIDER` edge counts, Databases reached via direct
 * `HAS_DATABASE` **or** (for gateways) `NETWORKS_WITH` → `db_server` →
 * `HAS_DATABASE`, the outbound `MAPS_TO_CONTROLLER` controllers, and
 * the inbound `(:Compute)-[:NETWORKS_WITH]->(this)` gateways. Ordered
 * case-insensitively by name so the grid is stable across page
 * renders.
 *
 * The remote Database hop mirrors `PLANT_COMPUTE_DETAIL_CYPHER` /
 * `mapDatabases`: JDBC catalogs hang on a synthetic `db_server`
 * Compute, so Ignition hosts would otherwise show a zero count.
 *
 * Drained client-side via `drainCypherRows` because the trailing
 * `SKIP toInteger($skip) LIMIT toInteger($pageSize)` is what walks the
 * result one page at a time — see `ontology-api-base.ts` for the full
 * rationale.
 */
export const PLANT_COMPUTE_INDEX_CYPHER = `
            MATCH (c:Compute)
            OPTIONAL MATCH (c)-[:HAS_TAG_PROVIDER]->(tp:TagProvider)
            WITH c, count(distinct tp) AS tagProviderCount
            OPTIONAL MATCH (c)-[:HAS_DATABASE]->(dbDirect:Database)
            WITH c, tagProviderCount, collect(distinct dbDirect) AS directDbs
            OPTIONAL MATCH (c)-[:NETWORKS_WITH]->(:Compute)-[:HAS_DATABASE]->(dbRemote:Database)
            WITH c, tagProviderCount, directDbs, collect(distinct dbRemote) AS remoteDbs
            WITH c, tagProviderCount,
                 size([x IN (directDbs + remoteDbs) WHERE x IS NOT NULL]) AS databaseCount
            OPTIONAL MATCH (c)-[:MAPS_TO_CONTROLLER]->(ctrl:Controller)
            WITH c, tagProviderCount, databaseCount, collect(distinct ctrl) AS controllers
            OPTIONAL MATCH (c)-[:MAPS_TO_CONTROLLER]->(dcs:DcsController)
            WITH c, tagProviderCount, databaseCount, controllers, collect(distinct dcs) AS dcsControllers
            OPTIONAL MATCH (gw:Compute)-[:NETWORKS_WITH]->(c)
            RETURN c,
                   tagProviderCount,
                   databaseCount,
                   controllers,
                   dcsControllers,
                   collect(distinct gw) AS gateways
            ORDER BY toLower(coalesce(c.name, c.id))
            SKIP toInteger($skip) LIMIT toInteger($pageSize)
          `

/**
 * Standalone `:Database` nodes that are not hung off a `:Compute`
 * (tabular-dump ingest MERGEs `urn:db:<name>` with no `HAS_DATABASE`).
 * Projected as `db_server` Compute-shaped maps so they land in the
 * Database Servers section beside JDBC catalogs.
 *
 * Drained separately from {@link PLANT_COMPUTE_INDEX_CYPHER} and merged
 * client-side — UNION of a node and a map in one Cypher column is
 * type-unsafe on Neo4j 5.
 */
export const PLANT_STANDALONE_DATABASE_INDEX_CYPHER = `
            MATCH (d:Database)
            WHERE NOT (d)<-[:HAS_DATABASE]-(:Compute)
            RETURN {
                     id: d.id,
                     name: coalesce(d.name, d.id),
                     url: d.url,
                     compute_type: 'db_server',
                     source_kind: d.source_kind,
                     database_type: d.database_type
                   } AS c,
                   0 AS tagProviderCount,
                   1 AS databaseCount,
                   [] AS controllers,
                   [] AS gateways
            ORDER BY toLower(coalesce(d.name, d.id))
            SKIP toInteger($skip) LIMIT toInteger($pageSize)
          `
